import { useFindOne, useGlobalAction } from "@gadgetinc/react";
import { message } from "antd";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../api";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { Badge } from "../../../externalComponents/tailwind/badge";
import AutocompleteInput from "../../../externalComponents/tailwind/autocomplete-input";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../externalComponents/tailwind/dialog";
import { Link } from "../../../externalComponents/tailwind/link";
import { Text } from "../../../externalComponents/tailwind/text";
import { CustomerContext } from "../../../providers";

const NOT_MAPPED = "";

const FIELD_TYPE_LABELS = {
  TextInput: "Text input",
  TextArea: "Text input",
  Dropdown: "Selector",
  RadioButtonsGroup: "Selector",
  CheckboxGroup: "Multiselector",
  ChipsSelector: "Multiselector",
  DatePicker: "Date picker",
  OptIn: "Opt-in",
  PhotoPicker: "File upload",
  DocumentPicker: "File upload",
};

const MULTISELECTOR_FIELD_TYPES = new Set(["CheckboxGroup", "ChipsSelector"]);

function ScreenFieldRow({ row, options, value, onChange }) {
  const selectedOption =
    options.find((option) => option.value === value) || null;
  const displayText = selectedOption?.label || value || "";
  const [searchText, setSearchText] = useState(displayText);

  useEffect(() => {
    setSearchText(displayText);
  }, [displayText]);

  const typeLabel = FIELD_TYPE_LABELS[row.fieldType] || row.fieldType;
  const rowLabel = `Screen ${row.screenPosition + 1} — ${row.label || row.screenField}${typeLabel ? ` (${typeLabel})` : ""}`;
  const isMultiselector = MULTISELECTOR_FIELD_TYPES.has(row.fieldType);

  return (
    <div className="flex items-center gap-3">
      <div
        className="flex-1 min-w-0 truncate bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm text-gray-700"
        title={rowLabel}
      >
        {rowLabel}
      </div>
      <span className="text-gray-400">→</span>
      <div className="flex-1">
        {isMultiselector ? (
          <div
            className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-sm text-gray-400"
            title="Multiselector answers can't be mapped to a custom field"
          >
            Not mappable
          </div>
        ) : (
          <AutocompleteInput
            value={searchText}
            onChange={setSearchText}
            options={options}
            selected={selectedOption}
            onSelect={(option) => {
              onChange(option.value);
              setSearchText(option.label);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default function WhatsappFlowMappingModal({
  flowId,
  open,
  onClose,
  onSaved,
}) {
  const { customer } = useContext(CustomerContext);
  const [mappings, setMappings] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [{ data, fetching, error }, syncWhatsappFlowScreens] = useGlobalAction(
    api.settings.syncWhatsappFlowScreens,
  );

  const [{ data: flowData }] = useFindOne(api.whatsappFlow, flowId, {
    select: {
      id: true,
      whatsappTemplate: {
        edges: {
          node: {
            id: true,
            campaign: {
              id: true,
              name: true,
              scenario: { type: true },
            },
            flowNode: {
              scenario: {
                type: true,
                campaign: { id: true, name: true },
              },
            },
          },
        },
      },
    },
  });

  const campaigns = useMemo(() => {
    const templates =
      flowData?.whatsappTemplate?.edges?.map((edge) => edge.node) || [];
    return Array.from(
      new Map(
        templates
          .map((t) => {
            const campaign = t.campaign || t.flowNode?.scenario?.campaign;
            if (!campaign) return null;
            const scenarioType =
              t.campaign?.scenario?.type || t.flowNode?.scenario?.type;
            return { ...campaign, scenario: { type: scenarioType } };
          })
          .filter(Boolean)
          .map((campaign) => [campaign.id, campaign]),
      ).values(),
    );
  }, [flowData]);

  const syncedFlowIdRef = useRef(null);

  useEffect(() => {
    if (open && flowId && syncedFlowIdRef.current !== flowId) {
      syncedFlowIdRef.current = flowId;
      syncWhatsappFlowScreens({ whatsappFlowId: flowId });
    }
  }, [open, flowId]);

  useEffect(() => {
    if (data?.rows) {
      setMappings(
        Object.fromEntries(
          data.rows.map((row) => [row.id, row.customField || NOT_MAPPED]),
        ),
      );
    }
  }, [data]);

  const customFieldOptions = useMemo(() => {
    const options = Object.keys(customer?.customFields || {})
      .filter(Boolean)
      .map((name) => ({ label: name, value: name }));

    return [{ label: "Not mapped", value: NOT_MAPPED }, ...options];
  }, [customer?.customFields]);

  const rows = useMemo(
    () =>
      [...(data?.rows || [])].sort(
        (a, b) => a.screenPosition - b.screenPosition,
      ),
    [data],
  );

  const handleClose = () => {
    setMappings({});
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const changedRows = (data?.rows || []).filter(
        (row) => (row.customField || NOT_MAPPED) !== mappings[row.id],
      );

      await Promise.all(
        changedRows.map((row) =>
          api.whatsappFlowScreens.update(row.id, {
            customField: mappings[row.id] || null,
          }),
        ),
      );

      message.success("Flow field mapping saved.");
      onSaved?.();
      handleClose();
    } catch (error) {
      message.error(`Failed to save flow mapping: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="2xl">
      <DialogTitle>{data?.flowName || "Map WhatsApp flow fields"}</DialogTitle>
      <DialogBody>
        {fetching ? (
          <LoadingSpinner />
        ) : error ? (
          <Text className="text-sm text-red-600">{error.message}</Text>
        ) : (
          <>
            <div className="mb-4">
              <Text className="text-sm font-semibold text-gray-700 mb-1">
                Templates using this flow
              </Text>
              {data?.matchedTemplateNames?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {data.matchedTemplateNames.map((name) => (
                    <Badge key={name} color="zinc">
                      {name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <Text className="text-sm text-gray-500">
                  No templates currently reference this flow.
                </Text>
              )}
            </div>

            <div className="mb-4">
              <Text className="text-sm font-semibold text-gray-700 mb-1">
                Campaigns using this flow
              </Text>
              {campaigns.length > 0 ? (
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {campaigns.map((campaign) => (
                    <Link
                      key={campaign.id}
                      href={`/${campaign.scenario?.type === "automation" ? "automations" : "campaigns"}/edit/${campaign.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pink hover:underline text-sm"
                    >
                      {campaign.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <Text className="text-sm text-gray-500">
                  No campaigns currently reference this flow.
                </Text>
              )}
            </div>

            <Text className="text-sm font-semibold text-gray-700 mb-1">
              Map flow fields to custom fields
            </Text>
            <Text className="text-sm text-gray-500 mb-3">
              Fields are extracted from the flow JSON. Map each to a custom
              field, or leave it as "Not mapped".
            </Text>

            <div className="flex flex-col gap-2">
              {rows.map((row) => (
                <ScreenFieldRow
                  key={row.id}
                  row={row}
                  options={customFieldOptions}
                  value={mappings[row.id] ?? NOT_MAPPED}
                  onChange={(value) =>
                    setMappings((prev) => ({ ...prev, [row.id]: value }))
                  }
                />
              ))}
            </div>
          </>
        )}
      </DialogBody>
      <DialogActions>
        <Button
          color="secondaryButton"
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          color="primaryButton"
          onClick={handleSave}
          loading={isSaving}
          disabled={fetching || !!error}
        >
          Save mapping
        </Button>
      </DialogActions>
    </Dialog>
  );
}
